'use client';

import { useEffect, useRef, useCallback, type RefObject } from 'react';

interface UseInfiniteScrollOptions {
  hasMore: boolean;
  loading: boolean;
  onLoadMore: () => void;
  threshold?: number; // Distance from bottom in pixels to trigger load
  scrollRef?: RefObject<HTMLDivElement | null>; // Optional external scroll ref
}

export function useInfiniteScroll({ hasMore, loading, onLoadMore, threshold = 100, scrollRef: externalScrollRef }: UseInfiniteScrollOptions) {
  const internalScrollRef = useRef<HTMLDivElement | null>(null);
  const scrollElementRef = externalScrollRef || internalScrollRef;

  const checkIfNearBottom = useCallback(() => {
    if (!scrollElementRef.current || loading || !hasMore) return;

    const element = scrollElementRef.current;
    const scrollTop = element.scrollTop;
    const scrollHeight = element.scrollHeight;
    const clientHeight = element.clientHeight;

    // Check if we're near the bottom
    if (scrollTop + clientHeight >= scrollHeight - threshold) {
      onLoadMore();
    }
  }, [loading, hasMore, onLoadMore, threshold]);

  const handleScroll = useCallback(
    (event: Event) => {
      const target = event.target as HTMLElement;
      if (target === scrollElementRef.current) {
        checkIfNearBottom();
      }
    },
    [checkIfNearBottom],
  );

  useEffect(() => {
    const element = scrollElementRef.current;
    if (!element) return;

    element.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      element.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  // Separate effect to check on mount only once
  // Delayed to allow scroll restoration and data loading to complete
  useEffect(() => {
    const timer = setTimeout(() => {
      checkIfNearBottom();
    }, 100);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Return ref to attach to scrollable element
  return scrollElementRef;
}
